import { addDays, set, format } from 'date-fns';
import { FilterOption, DefaultFilterOption } from 'src/models/Filter';
import FORMAT_DATE_QUERY_STRING from 'src/constants/dateFormatQueryString';
import getDateFromDaysOffsets from './getDateFromDaysOffsets';

export interface SingularType {
  type: 'singular';
  data: FilterOption;
}

export interface MultipleType {
  type: 'multiple';
  data: Map<string, DefaultFilterOption>;
}

export type QueryReducerParam = SingularType | MultipleType;

function parseSingularCategories(data: FilterOption): string {
  if (data.categoryId === 'license') {
    return `filter[where][brokerlicense][in]=${data.queryKey}`;
  }

  if (data.categoryId === 'subagent') {
    return `filter[where][agencyId][in]=${data.queryKey}`;
  }

  if (data.categoryId === 'date') {
    if (data.id === 'range-date') {
      const startDateWithCustomTime = set(data.dates.start as Date, {
        hours: 5,
        minutes: 0,
        seconds: 0,
      });

      const endDateWithCustomTime = set(data.dates.end as Date, {
        hours: 5,
        minutes: 0,
        seconds: 0,
      });

      const endDatePlusOneDay = addDays(endDateWithCustomTime, 1);

      const startDateFormatted = format(
        startDateWithCustomTime,
        FORMAT_DATE_QUERY_STRING,
      );
      const endDateFormatted = format(
        endDatePlusOneDay,
        FORMAT_DATE_QUERY_STRING,
      );
      return `filter[where][and][createdat][gte]=${startDateFormatted}&filter[where][and][createdat][lte]=${endDateFormatted}`;
    }
    // set present date time to 05:00:00
    const presentDateWithCustomTime = set(new Date(), {
      hours: 5,
      minutes: 0,
      seconds: 0,
    });

    const startDate = getDateFromDaysOffsets(
      presentDateWithCustomTime,
      data.daysOffsets.start,
    );
    const endDate = getDateFromDaysOffsets(
      presentDateWithCustomTime,
      data.daysOffsets.end,
    );

    const startDateFormated = format(startDate, FORMAT_DATE_QUERY_STRING);
    const endDateFormated = format(endDate, FORMAT_DATE_QUERY_STRING);

    return `filter[where][and][createdat][gte]=${startDateFormated}&filter[where][and][createdat][lte]=${endDateFormated}`;
  }

  // categoryId can only be plan
  return `filter[where][QuotationProfile.Type][in]=${data.queryKey}`;
}

function parseMultipleCategories(
  data: Map<string, DefaultFilterOption>,
): string {
  const keys: string[] = [];
  let expireFilter = '';
  let categoryId = '';
  data.forEach((filter) => {
    categoryId = filter.categoryId;
    if (filter.id === 'expired') {
      expireFilter = `filter[where][expireat][lte]=${new Date().toISOString()}`;
    } else {
      keys.push(filter.queryKey);
    }
  });

  // case where no filter is active
  if (keys.every((ele) => ele === '') && expireFilter === '') {
    return '';
  }

  const keyString = keys.join(',');
  if (categoryId === 'origin') {
    return `filter[where][origin][in]=${keyString}`;
  }

  if (categoryId === 'status') {
    if (expireFilter !== '' && keys.length !== 0) {
      return `filter[where][statusid][in]=${keyString}&${expireFilter}`;
    }
    if (expireFilter !== '') {
      return expireFilter;
    }
    return `filter[where][statusid][in]=${keyString}`;
  }
  // categoryId can only be user
  return `filter[where][createdBy][in]=${keyString}`;
}

export default function queryReducer(target: QueryReducerParam): string {
  if (target.type === 'singular') {
    const { data } = target;

    if (data.id.includes('default')) {
      return '';
    }
    return parseSingularCategories(data);
  }

  const { data } = target;
  return parseMultipleCategories(data);
}

export function buildQueryString(
  singularCategories: Map<string, FilterOption>,
  multipleCategories: Map<string, Map<string, DefaultFilterOption>>,
): string {
  const queryStrings: string[] = [];

  // build query strings for singular category
  singularCategories.forEach((category) => {
    const queryReducerParam: QueryReducerParam = {
      type: 'singular',
      data: category,
    };
    const queryString = queryReducer(queryReducerParam);
    if (queryString !== '') {
      queryStrings.push(queryString);
    }
  });

  // build query strings for multiple category
  multipleCategories.forEach((category) => {
    const queryReducerParam: QueryReducerParam = {
      type: 'multiple',
      data: category,
    };
    const queryString = queryReducer(queryReducerParam);
    if (queryString !== '') {
      queryStrings.push(queryString);
    }
  });

  return `${queryStrings.join('&')}`;
}
